import type { CSSProperties } from 'react';
import { injectCollapse } from '../lib/collapse-columns';
import { COLUMNS_COLORS, type ColorToken } from '../lib/colors';
import { COLUMNS_SPACING_PX, type SpacingToken } from '../lib/spacing';
import { StructuredContentRenderer } from '../renderer';
import type { NodeProps, StructuredContent } from '../types';

const ALIGN_Y = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
} as const;

const DEFAULT_SPACING = { mobile: 3, desktop: '3-half' } as const;

type ColumnsStyle = CSSProperties & Record<`--sc-${string}`, string | undefined>;

export type ColumnsProps = NodeProps<{
  alignY?: keyof typeof ALIGN_Y;
  backgroundColor?: ColorToken;
  children: StructuredContent | StructuredContent[];
  collapse?: boolean;
  padding?: SpacingToken;
  spacing?: SpacingToken | { desktop: SpacingToken; mobile: SpacingToken };
}>;

export function Columns({ props }: ColumnsProps) {
  const collapse = props.collapse ?? false;
  const spacing = props.spacing ?? DEFAULT_SPACING;
  const mobileSpacing =
    typeof spacing === 'object' && spacing?.desktop !== undefined ? spacing.mobile : spacing;
  const desktopSpacing =
    typeof spacing === 'object' && spacing?.desktop !== undefined ? spacing.desktop : spacing;
  const gapMobile = COLUMNS_SPACING_PX[mobileSpacing];
  const gapDesktop = COLUMNS_SPACING_PX[desktopSpacing];
  const style: ColumnsStyle = {
    '--sc-cols-mobile': collapse ? 'minmax(0,1fr)' : 'repeat(12,minmax(0,1fr))',
    '--sc-gap-mobile': `${gapMobile}px`,
    '--sc-gap-desktop': `${gapDesktop}px`,
    alignItems: props.alignY ? ALIGN_Y[props.alignY] : undefined,
    padding: COLUMNS_SPACING_PX[props.padding ?? 0],
    backgroundColor: COLUMNS_COLORS[props.backgroundColor ?? 'transparent'],
  };

  return (
    <div
      data-slot="sc-columns"
      className="grid grid-cols-[var(--sc-cols-mobile)] gap-[var(--sc-gap-mobile)] lg:grid-cols-12 lg:gap-[var(--sc-gap-desktop)]"
      style={style}
    >
      <StructuredContentRenderer doc={injectCollapse(props.children, collapse)} />
    </div>
  );
}
